import type { ComponentProps, ComponentType } from "react";
import { useCallback, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  SignInPage,
  UserIdentity,
} from "@backstage/core-components";
import { configApiRef, useApi, type IdentityApi } from "@backstage/core-plugin-api";
import { dexAuthApiRef } from "../../apis";

const SIGN_IN_SCOPES = "openid profile email groups offline_access";

type SignInPageProps = ComponentProps<typeof SignInPage>;

type RedirectProps = {
  provider?: { title?: string; message?: string; [key: string]: unknown };
  onSignInSuccess(identity: IdentityApi): void;
  ErrorComponent?: ComponentType<{ error?: Error }>;
};

function DexRedirectSignInRedirect(props: RedirectProps) {
  const { onSignInSuccess, ErrorComponent, provider } = props;
  const authApi = useApi(dexAuthApiRef);
  const configApi = useApi(configApiRef);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<Error>();

  const completeSignIn = useCallback(async () => {
    try {
      const identityResponse = await authApi.getBackstageIdentity({
        optional: true,
      });

      if (!identityResponse) {
        return;
      }

      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (err) {
      setError(err as Error);
    } finally {
      setChecking(false);
    }
  }, [authApi, onSignInSuccess]);

  useEffect(() => {
    completeSignIn();
  }, [completeSignIn]);

  const startRedirectFlow = () => {
    setError(undefined);

    const backendBaseUrl = configApi.getString("backend.baseUrl");
    const appBaseUrl = configApi.getString("app.baseUrl");
    const env = configApi.getString("auth.environment");
    const redirectUrl = window.location.href;
    const params = new URLSearchParams({
      env,
      origin: appBaseUrl,
      flow: "redirect",
      redirectUrl,
      scope: SIGN_IN_SCOPES,
    });

    window.location.href = `${backendBaseUrl}/api/auth/oidc/start?${params.toString()}`;
  };

  if (checking) {
    return <Progress />;
  }

  return (
    <Page themeId="home">
      <Header title={configApi.getString("app.title")} />
      <Content>
        <Grid container justifyContent="center" spacing={2} component="ul">
          <Grid item xs={12} md={6} lg={4} component="li">
            <InfoCard
              title={provider?.title ?? "Sign in"}
              variant="fullHeight"
              actions={
                <Button color="primary" variant="outlined" onClick={startRedirectFlow}>
                  Continue
                </Button>
              }
            >
              <Typography variant="body1" paragraph>
                {provider?.message ??
                  "Use your corporate identity provider to sign in. Your Backstage identity will be created from your Dex / Google Workspace groups."}
              </Typography>
              {error &&
                (ErrorComponent ? (
                  <ErrorComponent error={error} />
                ) : (
                  <Typography variant="body1" color="error">
                    {error.message}
                  </Typography>
                ))}
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}

const DexRedirectSignInPage = (props: RedirectProps | SignInPageProps) => {
  if ("providers" in props) {
    return <SignInPage {...props} />;
  }

  return <DexRedirectSignInRedirect {...props} />;
};

export default DexRedirectSignInPage;
